import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

// Define custom color palette with new company colors
const customColors = {
  primary: {
    main: '#f92955', // New pink
    light: '#ff6b8a',
    dark: '#d41e3f',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#BBFFC1', // New green
    light: '#d4ffd8',
    dark: '#a8ffae',
    contrastText: '#000000',
  },
  success: {
    main: '#BBFFC1', // Using the green for success
    light: '#d4ffd8',
    dark: '#a8ffae',
    contrastText: '#000000',
  },
  warning: {
    main: '#FF9800',
    light: '#FFB74D',
    dark: '#F57C00',
    contrastText: '#000000',
  },
  error: {
    main: '#F44336',
    light: '#E57373',
    dark: '#D32F2F',
    contrastText: '#FFFFFF',
  },
  info: {
    main: '#2196F3',
    light: '#64B5F6',
    dark: '#1976D2',
    contrastText: '#FFFFFF',
  },
  grey: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
};

// Create base theme
let theme = createTheme({
  palette: {
    mode: 'light',
    primary: customColors.primary,
    secondary: customColors.secondary,
    success: customColors.success,
    warning: customColors.warning,
    error: customColors.error,
    info: customColors.info,
    background: {
      default: '#FFFFFF', // Pure white background
      paper: '#FFFFFF',
      alternate: '#f92955', // Pink for alternating sections
      card: '#f92955', // Pink for card backgrounds
    },
    text: {
      primary: '#000000', // Pure black for maximum readability
      secondary: '#424242',
      disabled: '#9E9E9E',
      icon: '#BBFFC1', // Green for icons
    },
    divider: 'rgba(0, 0, 0, 0.08)',
    action: {
      active: 'rgba(0, 0, 0, 0.54)',
      hover: 'rgba(249, 41, 85, 0.04)', // Pink hover
      selected: 'rgba(249, 41, 85, 0.08)', // Pink selected
      disabled: 'rgba(0, 0, 0, 0.26)',
      disabledBackground: 'rgba(0, 0, 0, 0.12)',
    },
    charts: {
      // Custom colors for charts and visualizations
      blue: '#2196F3',
      green: '#BBFFC1', // Updated to new green
      orange: '#FF9800',
      purple: '#9C27B0',
      cyan: '#00BCD4',
      yellow: '#FFC107',
      pink: '#f92955', // Updated to new pink
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 800,
      fontSize: '3rem',
      lineHeight: 1.1,
      letterSpacing: '-0.02em',
      color: '#000000',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2.25rem',
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
      color: '#000000',
    },
    h3: {
      fontWeight: 700,
      fontSize: '1.875rem',
      lineHeight: 1.2,
      letterSpacing: '0em',
      color: '#000000',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.3,
      letterSpacing: '0.00735em',
      color: '#000000',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
      letterSpacing: '0em',
      color: '#000000',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.4,
      letterSpacing: '0.0075em',
      color: '#000000',
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: '1.125rem',
      lineHeight: 1.5,
      letterSpacing: '0.00938em',
      color: '#424242',
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: '1rem',
      lineHeight: 1.5,
      letterSpacing: '0.00714em',
      color: '#424242',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      letterSpacing: '0.00938em',
      color: '#000000',
    },
    body2: {
      fontSize: '0.9375rem',
      lineHeight: 1.6,
      letterSpacing: '0.01071em',
      color: '#424242',
    },
    button: {
      fontWeight: 600,
      fontSize: '0.9375rem',
      lineHeight: 1.75,
      letterSpacing: '0.02857em',
      textTransform: 'none',
      color: '#000000',
    },
    caption: {
      fontSize: '0.8125rem',
      lineHeight: 1.5,
      letterSpacing: '0.03333em',
      color: '#757575',
    },
    overline: {
      fontSize: '0.75rem',
      lineHeight: 2.5,
      letterSpacing: '0.08333em',
      textTransform: 'uppercase',
      fontWeight: 500,
      color: '#757575',
    },
  },
  shape: {
    borderRadius: 12, // More rounded corners for modern look
  },
  shadows: [
    'none',
    '0px 2px 4px -1px rgba(0,0,0,0.06),0px 1px 2px 0px rgba(0,0,0,0.04),0px 1px 3px 0px rgba(0,0,0,0.03)',
    '0px 3px 6px -2px rgba(0,0,0,0.06),0px 2px 8px 0px rgba(0,0,0,0.04),0px 1px 10px 0px rgba(0,0,0,0.03)',
    '0px 4px 8px -2px rgba(0,0,0,0.06),0px 3px 12px 0px rgba(0,0,0,0.04),0px 1px 14px 0px rgba(0,0,0,0.03)',
    '0px 6px 12px -3px rgba(0,0,0,0.06),0px 4px 16px 0px rgba(0,0,0,0.04),0px 2px 18px 0px rgba(0,0,0,0.03)',
    '0px 8px 16px -4px rgba(0,0,0,0.06),0px 6px 20px 0px rgba(0,0,0,0.04),0px 3px 22px 0px rgba(0,0,0,0.03)',
    '0px 10px 20px -5px rgba(0,0,0,0.06),0px 8px 24px 0px rgba(0,0,0,0.04),0px 4px 26px 0px rgba(0,0,0,0.03)',
    '0px 12px 24px -6px rgba(0,0,0,0.06),0px 10px 28px 0px rgba(0,0,0,0.04),0px 5px 30px 0px rgba(0,0,0,0.03)',
    '0px 14px 28px -7px rgba(0,0,0,0.06),0px 12px 32px 0px rgba(0,0,0,0.04),0px 6px 34px 0px rgba(0,0,0,0.03)',
    '0px 16px 32px -8px rgba(0,0,0,0.06),0px 14px 36px 0px rgba(0,0,0,0.04),0px 7px 38px 0px rgba(0,0,0,0.03)',
    '0px 18px 36px -9px rgba(0,0,0,0.06),0px 16px 40px 0px rgba(0,0,0,0.04),0px 8px 42px 0px rgba(0,0,0,0.03)',
    '0px 20px 40px -10px rgba(0,0,0,0.06),0px 18px 44px 0px rgba(0,0,0,0.04),0px 9px 46px 0px rgba(0,0,0,0.03)',
    '0px 22px 44px -11px rgba(0,0,0,0.06),0px 20px 48px 0px rgba(0,0,0,0.04),0px 10px 50px 0px rgba(0,0,0,0.03)',
    '0px 24px 48px -12px rgba(0,0,0,0.06),0px 22px 52px 0px rgba(0,0,0,0.04),0px 11px 54px 0px rgba(0,0,0,0.03)',
    '0px 26px 52px -13px rgba(0,0,0,0.06),0px 24px 56px 0px rgba(0,0,0,0.04),0px 12px 58px 0px rgba(0,0,0,0.03)',
    '0px 28px 56px -14px rgba(0,0,0,0.06),0px 26px 60px 0px rgba(0,0,0,0.04),0px 13px 62px 0px rgba(0,0,0,0.03)',
    '0px 30px 60px -15px rgba(0,0,0,0.06),0px 28px 64px 0px rgba(0,0,0,0.04),0px 14px 66px 0px rgba(0,0,0,0.03)',
    '0px 32px 64px -16px rgba(0,0,0,0.06),0px 30px 68px 0px rgba(0,0,0,0.04),0px 15px 70px 0px rgba(0,0,0,0.03)',
    '0px 34px 68px -17px rgba(0,0,0,0.06),0px 32px 72px 0px rgba(0,0,0,0.04),0px 16px 74px 0px rgba(0,0,0,0.03)',
    '0px 36px 72px -18px rgba(0,0,0,0.06),0px 34px 76px 0px rgba(0,0,0,0.04),0px 17px 78px 0px rgba(0,0,0,0.03)',
    '0px 38px 76px -19px rgba(0,0,0,0.06),0px 36px 80px 0px rgba(0,0,0,0.04),0px 18px 82px 0px rgba(0,0,0,0.03)',
    '0px 40px 80px -20px rgba(0,0,0,0.06),0px 38px 84px 0px rgba(0,0,0,0.04),0px 19px 86px 0px rgba(0,0,0,0.03)',
    '0px 42px 84px -21px rgba(0,0,0,0.06),0px 40px 88px 0px rgba(0,0,0,0.04),0px 20px 90px 0px rgba(0,0,0,0.03)',
    '0px 44px 88px -22px rgba(0,0,0,0.06),0px 42px 92px 0px rgba(0,0,0,0.04),0px 21px 94px 0px rgba(0,0,0,0.03)',
    '0px 46px 92px -23px rgba(0,0,0,0.06),0px 44px 96px 0px rgba(0,0,0,0.04),0px 22px 98px 0px rgba(0,0,0,0.03)',
    '0px 48px 96px -24px rgba(0,0,0,0.06),0px 46px 100px 0px rgba(0,0,0,0.04),0px 23px 102px 0px rgba(0,0,0,0.03)',
  ],
  spacing: 8, // Base spacing unit in pixels
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          height: '100%',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        body: {
          height: '100%',
          fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
          fontSize: '1rem',
          lineHeight: 1.6,
          color: '#000000',
          backgroundColor: '#FFFFFF',
        },
        '#root': {
          height: '100%',
        },
        '::-webkit-scrollbar': {
          width: '10px',
          height: '10px',
        },
        '::-webkit-scrollbar-track': {
          background: '#F5F5F5',
          borderRadius: '5px',
        },
        '::-webkit-scrollbar-thumb': {
          background: '#E0E0E0',
          borderRadius: '5px',
          '&:hover': {
            background: '#BDBDBD',
          },
        },
        '*': {
          boxSizing: 'border-box',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FFFFFF',
          color: '#000000',
          borderRight: '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: '4px 0 20px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          color: '#000000',
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.04)',
        },
      },
      defaultProps: {
        elevation: 0,
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '12px 24px',
          boxShadow: 'none',
          fontWeight: 600,
          fontSize: '0.9375rem',
          textTransform: 'none',
          transition: 'all 0.2s ease-in-out',
                  '&:hover': {
          transform: 'translateY(-1px)',
        },
      },
      contained: {
        boxShadow: '0px 2px 8px rgba(249, 41, 85, 0.15)',
        '&:hover': {
          boxShadow: '0px 4px 16px rgba(249, 41, 85, 0.25)',
        },
      },
        outlined: {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
          },
        },
      },
      defaultProps: {
        disableElevation: true,
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.06)',
          overflow: 'hidden',
          border: '1px solid rgba(0, 0, 0, 0.04)',
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: '24px 32px',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
        },
        title: {
          fontSize: '1.25rem',
          fontWeight: 700,
          color: '#000000',
        },
        subheader: {
          fontSize: '1rem',
          color: '#424242',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '32px',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
          padding: '16px 24px',
          fontSize: '0.9375rem',
        },
        head: {
          fontWeight: 700,
          color: '#000000',
          backgroundColor: alpha(customColors.primary.main, 0.04),
          fontSize: '0.875rem',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: alpha(customColors.primary.main, 0.02),
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          padding: '12px 16px',
          fontSize: '0.8125rem',
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          fontSize: '0.8125rem',
          height: 32,
          fontWeight: 600,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.9375rem',
          minWidth: 120,
          padding: '16px 24px',
          color: '#424242',
                  '&.Mui-selected': {
          color: '#f92955',
        },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
              indicator: {
        height: 4,
        borderRadius: 2,
        backgroundColor: '#f92955',
      },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: alpha(customColors.primary.main, 0.12),
          height: 8,
        },
        bar: {
          borderRadius: 8,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '16px 20px',
          fontSize: '0.9375rem',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          minHeight: 48,
          fontSize: '0.9375rem',
          fontWeight: 500,
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 48,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          margin: '4px 8px',
          '&.Mui-selected': {
            backgroundColor: alpha(customColors.primary.main, 0.08),
            '&:hover': {
              backgroundColor: alpha(customColors.primary.main, 0.12),
            },
          },
          '&:hover': {
            backgroundColor: alpha(customColors.primary.main, 0.04),
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          border: `2px solid ${alpha('#fff', 0.2)}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0px 2px 12px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          padding: 8,
        },
        track: {
          borderRadius: 22 / 2,
        },
        thumb: {
          boxShadow: 'none',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 0,
          fontSize: '0.8125rem',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            fontSize: '0.9375rem',
          },
        },
      },
    },
  },
});

// Add responsive typography
theme = responsiveFontSizes(theme);

// Create a function to toggle between light and dark modes
export const getTheme = (mode = 'light') => {
  if (mode === 'dark') {
    return createTheme({
      ...theme,
      palette: {
        ...theme.palette,
        mode: 'dark',
        primary: customColors.primary,
        secondary: customColors.secondary,
        background: {
          default: '#000000', // Pure black background
          paper: '#1A1A1A', // Dark paper background
          alternate: '#0F0F0F', // For striped tables or alternating sections
        },
        text: {
          primary: '#FFFFFF',
          secondary: '#E0E0E0',
          disabled: '#757575',
        },
        divider: 'rgba(255, 255, 255, 0.12)',
        action: {
          active: 'rgba(255, 255, 255, 0.7)',
          hover: 'rgba(233, 30, 99, 0.08)',
          selected: 'rgba(233, 30, 99, 0.16)',
          disabled: 'rgba(255, 255, 255, 0.3)',
          disabledBackground: 'rgba(255, 255, 255, 0.12)',
        },
      },
      components: {
        ...theme.components,
        MuiAppBar: {
          styleOverrides: {
            root: {
              backgroundColor: '#1A1A1A',
              color: '#FFFFFF',
              boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.2)',
            },
          },
        },
        MuiDrawer: {
          styleOverrides: {
            paper: {
              backgroundColor: '#000000',
              color: '#FFFFFF',
              borderRight: '1px solid rgba(255, 255, 255, 0.12)',
            },
          },
        },
        MuiTableCell: {
          styleOverrides: {
            root: {
              borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            },
            head: {
              color: '#FFFFFF',
              backgroundColor: alpha(customColors.primary.dark, 0.2),
            },
          },
        },
        MuiTableRow: {
          styleOverrides: {
            root: {
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.04)',
              },
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: 'none',
            },
          },
        },
      },
    });
  }
  return theme;
};

export default getTheme();